'use client';

import { c as _c } from "react/compiler-runtime";
import { EmptyState, TimelineItemSkeleton } from '@/shared/components/ui';
import { FeatherClock } from '@subframe/core';
import { useNegociacaoMarcos } from '../../../hooks/useNegociacaoMarcos';
import type { MarcosViewState } from '../../../types/negociacaoMarcoTypes';
import { agruparMarcosPorDia } from '../../../utils/marcoDataHora';
import { MarcoCard } from './MarcoCard';
import { MarcosDuracaoBadge } from './MarcosDuracaoBadge';
import { MarcosErroState } from './MarcosErroState';
interface NegociacaoMarcosTabProps {
  casoId: string | null | undefined;
  dataCriacao: string | null | undefined;
  dataEncerramento: string | null | undefined;
}
const QUANTIDADE_SKELETONS = 3;
function ConteudoMarcos(t0) {
  const $ = _c(16);
  const {
    viewState,
    sentinelRef,
    tentarNovamente
  } = t0;
  switch (viewState.estado) {
    case "carregando":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <div className="flex flex-col" data-testid="marcos-skeleton">{Array.from({
              length: QUANTIDADE_SKELETONS
            }, _temp)}</div>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "erro":
      {
        let t1;
        if ($[1] !== tentarNovamente || $[2] !== viewState.sugerirSuporte) {
          t1 = <MarcosErroState sugerirSuporte={viewState.sugerirSuporte} onTentarNovamente={tentarNovamente} />;
          $[1] = tentarNovamente;
          $[2] = viewState.sugerirSuporte;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "vazio":
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <EmptyState icon={<FeatherClock className="text-neutral-500" />} title="Nenhum marco registrado ainda" description={"Os marcos aparecer\xE3o aqui conforme a negocia\xE7\xE3o avan\xE7ar."} className="py-12" />;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "lista":
      {
        let t1;
        if ($[5] !== viewState.items) {
          t1 = agruparMarcosPorDia(viewState.items).map(_temp2);
          $[5] = viewState.items;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        let t2;
        if ($[7] !== sentinelRef || $[8] !== viewState.hasMore) {
          t2 = viewState.hasMore && <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />;
          $[7] = sentinelRef;
          $[8] = viewState.hasMore;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        let t3;
        if ($[10] !== viewState.carregandoMais) {
          t3 = viewState.carregandoMais && <div data-testid="marcos-carregando-mais"><TimelineItemSkeleton /></div>;
          $[10] = viewState.carregandoMais;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        let t4;
        if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
          t4 = <div className="flex flex-col">{t1}{t2}{t3}</div>;
          $[12] = t1;
          $[13] = t2;
          $[14] = t3;
          $[15] = t4;
        } else {
          t4 = $[15];
        }
        return t4;
      }
  }
}
function _temp2(grupo) {
  return <section key={grupo.items[0].id} aria-label={grupo.rotulo} className="pt-5 first:pt-0"><div className="mb-2 flex items-center gap-3" data-testid="marcos-dia"><span className="min-w-0 rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">{grupo.rotulo}</span><span className="min-w-0 flex-1 border-t border-neutral-200" aria-hidden="true" /></div>{grupo.items.map((marco, indice) => <MarcoCard key={marco.id} marco={marco} isLast={indice === grupo.items.length - 1} />)}</section>;
}
function _temp(_, indice_0) {
  return <TimelineItemSkeleton key={indice_0} />;
}
export function NegociacaoMarcosTab(t0) {
  const $ = _c(15);
  const {
    casoId,
    dataCriacao,
    dataEncerramento
  } = t0;
  const {
    viewState,
    sentinelRef,
    tentarNovamente
  } = useNegociacaoMarcos(casoId);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="text-heading-3 font-heading-3 text-default-font">Marcos da Negociação</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== dataCriacao || $[2] !== dataEncerramento || $[3] !== viewState.estado) {
    t2 = viewState.estado === "lista" && <MarcosDuracaoBadge dataCriacao={dataCriacao} dataEncerramento={dataEncerramento} />;
    $[1] = dataCriacao;
    $[2] = dataEncerramento;
    $[3] = viewState.estado;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="mb-2 flex items-center justify-between gap-4 border-b border-solid border-neutral-100 pb-4">{t1}{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-caption text-subtext-color mb-8">Eventos importantes do fluxo, como ofertas, decisões e acordos.</p>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== sentinelRef || $[9] !== tentarNovamente || $[10] !== viewState) {
    t5 = <ConteudoMarcos viewState={viewState} sentinelRef={sentinelRef} tentarNovamente={tentarNovamente} />;
    $[8] = sentinelRef;
    $[9] = tentarNovamente;
    $[10] = viewState;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <div className="flex w-full flex-col"><div className="rounded-[16px] border border-solid border-neutral-200 bg-white p-8 shadow-sm">{t3}{t4}{t5}</div></div>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}